import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap, shareReplay, finalize, switchMap, startWith } from "rxjs/operators";
import { RoleService } from "./role.service";
import { Role } from "@models/role.model";

@Injectable({
  providedIn: "root",
})
export class RoleStateService {
  private rolesSubject = new BehaviorSubject<any[]>([]);
  private loadingRequest$: Observable<any> | null = null;
  private isInitialized = false; // Smart observable that auto-loads roles when subscribed to
  public roles$ = new Observable((subscriber) => {
    // Auto-initialize if not already done
    if (!this.isInitialized && this.getCurrentRoles().length === 0) {
      this.initializeRoles().subscribe();
    }

    // Subscribe to the actual roles observable
    return this.rolesSubject.asObservable().subscribe(subscriber);
  });

  constructor(private roleService: RoleService) {}

  // Load initial roles
  loadRoles(): Observable<any> {
    return this.roleService.findAndFilter();
  }

  initializeRoles(): Observable<any> {
    // If already initialized, return current roles
    if (this.isInitialized) {
      return of(this.getCurrentRoles());
    }

    // If a request is already in progress, return the same observable
    if (this.loadingRequest$) {
      return this.loadingRequest$;
    }

    // Create a new request and cache it
    this.loadingRequest$ = this.loadRoles().pipe(
      tap((roles) => {
        this.rolesSubject.next(roles);
        this.isInitialized = true;
      }),
      finalize(() => {
        // Clear the loading request when complete
        this.loadingRequest$ = null;
      }),
      shareReplay(1) // Share the result with multiple subscribers
    );
    return this.loadingRequest$;
  }

  refreshRoles(): Observable<any> {
    this.isInitialized = false;
    this.loadingRequest$ = null;

    // Create a new request and execute it immediately
    this.loadingRequest$ = this.loadRoles().pipe(
      tap((roles) => {
        this.rolesSubject.next(roles);
        this.isInitialized = true;
      }),
      finalize(() => {
        // Clear the loading request when complete
        this.loadingRequest$ = null;
      }),
      shareReplay(1) // Share the result with multiple subscribers
    );

    return this.loadingRequest$;
  }

  // Get current roles synchronously
  getCurrentRoles(): any[] {
    return this.rolesSubject.value;
  }

  // Add a new role to the state
  addRole(role: any): void {
    const currentRoles = this.getCurrentRoles();
    this.rolesSubject.next([...currentRoles, role]);
  }

  // Update a role in the state
  updateRole(updatedRole: any): void {
    const currentRoles = this.getCurrentRoles();
    const index = currentRoles.findIndex((role) => role.id === updatedRole.id);
    if (index !== -1) {
      const newRoles = [...currentRoles];
      newRoles[index] = updatedRole;
      this.rolesSubject.next(newRoles);
    }
  }
  // Remove a role from the state
  removeRole(roleId: number | string): void {
    const currentRoles = this.getCurrentRoles();
    const filteredRoles = currentRoles.filter((role) => role.id !== roleId && role.uuid !== roleId);
    this.rolesSubject.next(filteredRoles);
  }
}
